import { UseQueryResult } from '@tanstack/react-query';
import { ColumnDef, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, Table, TableOptions, useReactTable } from '@tanstack/react-table';
import { useResourceList, useResourceListProps } from 'hooks/useResourceList';
import React, { ReactNode, useContext } from 'react';

type TableContextValue = {
	table: Table<any>;
  queryResult?: UseQueryResult<any>;
}

interface DynamicTableProviderProps {
  columns: ColumnDef<any>[];
  resource: useResourceListProps;
	children?: ReactNode;
  tableOptions?: Partial<TableOptions<any>>;
}

interface TableProviderProps {
  columns: ColumnDef<any>[];
  data: any[];
	children?: ReactNode;
  queryResult?: UseQueryResult<any>;
  tableOptions?: Partial<TableOptions<any>>;
}

const DynamicTableContext = React.createContext<TableContextValue | undefined>(
	undefined,
);

export const useDynamicTableContext = () => {
	const value = useContext(DynamicTableContext);
	if (value === undefined) {
		throw Error('useDynamicTableContext can not be used outside a DynamicTableProvider');
	}
	return value;
};

export default function DynamicTableProvider({ columns, resource, tableOptions, children }: DynamicTableProviderProps) {
  const queryResult = useResourceList({ ...resource });

  const { data = [] } = queryResult;

  return (
    <TableProvider 
      columns={columns}
      data={data}
      tableOptions={tableOptions}
      queryResult={queryResult}
    >
      {children}
    </TableProvider>
  );
}

export function TableProvider({ columns, data, queryResult, tableOptions = {}, children }: TableProviderProps) {
  const [globalFilter, setGlobalFilter] = React.useState<any>([]);
  

	const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 40,
  });

  const options: TableOptions<any> = {
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: 'includesString',
    state: {
      pagination,
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    ...tableOptions
  };

  if (!tableOptions.manualPagination) {
    options.onPaginationChange = setPagination;
  }
  const table = useReactTable(options);

	return (
		<DynamicTableContext.Provider
			value={{
				table,
        queryResult
			}}>
			{children}
		</DynamicTableContext.Provider>
	);
}